import * as React from 'react';
import { Checkbox as ShadcnCheckbox } from '@/hen-ui/shadcn/checkbox';
import { Label } from '@/hen-ui/atomics/Label';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnCheckbox> {
  label: React.ReactNode;
  /**
   * Unique ID for the checkbox input. If not provided, one will be generated.
   * Required when label contains complex content.
   */
  id?: string;
  error?: boolean;
  disabled?: boolean;
  checked?: boolean;
  /**
   * When true (CheckboxButton), the label is the outer surface so the full
   * button chrome — not only the control and text — toggles the checkbox.
   */
  isButton?: boolean;
}

/**
 * Checkbox component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui checkbox (Radix UI Checkbox primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Checkbox label="Default Checkbox" />
 *
 * @example
 * // Disabled Checkbox
 * <Checkbox label="Disabled Checkbox" disabled />
 *
 * @example
 * // Error Checkbox
 * <Checkbox label="Error Checkbox" error />
 *
 * @example
 * // With HTML content in label
 * <Checkbox
 *   id="terms"
 *   label={<>I agree to <a href="/terms">Terms & Conditions</a></>}
 * />
 *
 * @example
 * // With parsed HTML string
 * <Checkbox id="consent" label={parseHtmlString(htmlContent)} />
 */

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof ShadcnCheckbox>,
  CheckboxProps
>(({ className, label, id, error, disabled, isButton, ...props }, ref) => {
  // Generate unique ID if not provided
  const generatedId = React.useId();
  const checkboxId = id || generatedId;

  const labelClassName = cn(
    error && 'text-error-500',
    disabled && 'opacity-50',
    'font-normal',
  );

  const control = (
    <ShadcnCheckbox
      ref={ref}
      id={checkboxId}
      aria-invalid={error}
      disabled={disabled}
      data-hen-id={
        isButton ? COMPONENT_IDS.CheckboxButton : COMPONENT_IDS.Checkbox
      }
      {...props}
    />
  );

  // Button chrome (CheckboxButton) must be the associated label so padding and
  // empty flex space inside the bordered control toggle — not only the box/text.
  if (isButton) {
    return (
      <Label
        htmlFor={checkboxId}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center gap-xxs',
          !disabled && 'cursor-pointer',
          labelClassName,
          className,
        )}
      >
        {control}
        {label}
      </Label>
    );
  }

  return (
    <Flex align='center' gap='xxs' direction='row' className={className}>
      {control}
      <Label
        className={labelClassName}
        htmlFor={checkboxId}
        disabled={disabled}
      >
        {label}
      </Label>
    </Flex>
  );
});

Checkbox.displayName = 'Checkbox';
